import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { ClockIcon } from "../../../assets/Clock";
import { WalletIcon } from "../../../assets/Wallet";
import { LocationFooterIcon } from "../../../assets/footer/Location";
import { PhoneIcon } from "../../../assets/Phone";

import { Card } from "../../../components/ui/card";

import { useTelegramBackButton } from "../../../utils/BackButton";
import { useGetPaymentList } from "../services/getPaymentList";
import { useOrderCalculation } from "../../basket/services/useOrderCalculation";
import { OrderCalculationCard } from "../../basket/ui/OrderCalculationCard";
import { Skeleton } from "../../../components/ui/skeleton";
import { useDigitsMask } from "../../../components/form/useDigitsMask";
import {
  getPaymentAnalyticsType,
  getPaymentKind,
  resolveCashPayment,
} from "../libs/paymentMethods";
import { PaymentMethods, PaymentSelection } from "./PaymentMethods";
import { useGetAddresses } from "../../addresses/services/getAddresses";

import { useDispatch, useSelector } from "react-redux";

import { useNavigate } from "react-router-dom";
import { useGetDeliveryDetail } from "../../basket/services/getDeliveryDetail";
import { formatPrice } from "../../../utils/getPrice";
import {
  getBasketItemUnitPrice,
  selectBasketCalculation,
  selectBasketItems,
  selectTotalPrice,
} from "../../../store/selectors/basket";
import { useEffect, useMemo, useState } from "react";
import { DeliveryTimePicker } from "../../../components/DeliveryTimePicker";
import { cn } from "../../../lib/utils";
import { impactOccuer } from "../../../utils/hapticEvents";
import { useCreateOrder } from "../../basket/services/createOrder";
import { getStoredOrderExtras } from "../../basket/libs/orderExtrasStorage";
import { LoadingButton } from "../../../components/ui/loadingButton";
import { userInfo } from "../../../store/selectors/user";

import { userSavedAddresses } from "../../../store/selectors/userAddresses";
import { getPickup } from "../../../store/selectors/pickup";
import { setPickupOn } from "../../../store/slices/pickup";
import { isPickupSelected } from "../../../store/selectors/fulfillment";
import { FulfillmentMode } from "../../../store/slices/fulfillment";
import { CommentIcon } from "../../../assets/Comment";
import { useGetCardList } from "../../userCard/services/getAll";
import { ChevronRight, Loader2, X } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { SavaeCardDrawer } from "../../../components/SaveCardDrawer";
import { useGetMaxAmout } from "../services/getMaxAmount";
import { useGetNearesBranch } from "../../addresses/services/getNearBranch";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "../../../components/ui/drawer";
import { getActiveWorkWindow } from "../../../utils/workingHours";
import { getPickupMinTime, getPickupMaxTime } from "../../../utils/pickupWindow";
import { validateScheduledTime } from "../../../utils/scheduledTimeValidation";

const getPhoneDigits = (value?: string) => {
  const digits = (value || "").replace(/\D/g, "");
  return digits.startsWith("998") ? digits.slice(3, 12) : digits.slice(-9);
};

const normalizePhoneNumber = (value?: string) => {
  const digits = getPhoneDigits(value);
  return digits.length === 9 ? `+998${digits}` : "+998901234567";
};

const formatRecipientPhone = (value?: string) => {
  const digits = getPhoneDigits(value);
  if (digits.length !== 9) return value || "+998 90 123-4567";

  return `+998 ${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(5)}`;
};

const formatPhoneInput = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  const parts = [
    digits.slice(0, 2),
    digits.slice(2, 5),
    digits.slice(5, 7),
    digits.slice(7, 9),
  ].filter(Boolean);

  return parts.join(" ");
};

export const Checkout = () => {
  const [open, setOpen] = useState(false);
  const [phoneDrawerOpen, setPhoneDrawerOpen] = useState(false);
  // Выбранный способ оплаты. Только из актуального branch/payment-list:
  // сменился список — выбор, которого в нём больше нет, сбрасывается ниже.
  const [paymentSelection, setPaymentSelection] =
    useState<PaymentSelection | null>(null);
  const [deliveryOn, setDeliveryOn] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showPickupTimePicker, setShowPickupTimePicker] = useState(false);
  // Пока только выбор в интерфейсе: в /order/create поля под «на вынос /
  // в ресторане» нет, отправлять некуда.
  const [receivingMode, setReceivingMode] = useState<"takeaway" | "in-place">(
    "takeaway",
  );
  // Set when the previously chosen time no longer clears our checks at the
  // moment of submitting (e.g. the user picked a time, then sat on this page
  // long enough for it to slip out of range) — shown inside the reopened
  // time drawer so the user knows why they're being asked to repick.
  const [timeIssue, setTimeIssue] = useState<"too-soon" | "too-late" | null>(
    null,
  );
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { amount, isLoading: isLoadingMaxAmount } = useGetMaxAmout();

  const user = useSelector(userInfo);
  const [recipientPhone, setRecipientPhone] = useState(() =>
    normalizePhoneNumber(user.phone),
  );
  const [phoneDraft, setPhoneDraft] = useState(() =>
    getPhoneDigits(user.phone || recipientPhone),
  );
  // Маска с сохранением каретки: без неё backspace в середине номера стирал
  // последнюю цифру, а не ту, что под курсором.
  const phoneMask = useDigitsMask({
    digits: phoneDraft,
    format: formatPhoneInput,
    maxLength: 9,
    onChange: setPhoneDraft,
  });
  const { nearest } = useGetNearesBranch();
  const isPickup = useSelector(isPickupSelected);
  const pickup = useSelector(getPickup);
  // A pickup order is fulfilled by the branch the user picked on the map,
  // not by whichever branch happens to be nearest to them.
  const branchId =
    (isPickup ? Number(pickup.branch._id) : nearest?.branch_id) ?? 0;
  // Derived from `isPickup` rather than the raw tab: a pickup tab without a
  // chosen branch is still a delivery order everywhere else in this screen.
  const orderType: FulfillmentMode = isPickup ? "pickup" : "delivery";
  const {
    paymentList,
    isLoading: isLoadingPayments,
    isError: isPaymentListError,
    isReady: isPaymentListReady,
    refetch: refetchPaymentList,
  } = useGetPaymentList(branchId ? String(branchId) : undefined, orderType);
  const navigate = useNavigate();
  const savedAddresses = useSelector(userSavedAddresses);
  const { cardList, isLoading: isLoadingCardList } = useGetCardList();

  const { addresses, isLoading: isLoadingAddresses } = useGetAddresses();

  const totalPrice = useSelector(selectTotalPrice);
  const {
    calculatedTotalPrice,
    calculatedDeliveryPrice,
    activePromoCode,
    selectedBonusPay,
  } = useSelector(selectBasketCalculation);
  const { deliveryDetail } = useGetDeliveryDetail(
    totalPrice,
    branchId,
  );
  const basketItems = useSelector(selectBasketItems);
  const { createOrder, isPending } = useCreateOrder();
  const availableBasketItems = useMemo(
    () => basketItems.filter((item) => item.isAvailable !== false),
    [basketItems],
  );

  useEffect(() => {
    const normalizedPhone = normalizePhoneNumber(user.phone);
    setRecipientPhone((current) =>
      current === "+998901234567" ? normalizedPhone : current,
    );
    setPhoneDraft((current) => current || getPhoneDigits(normalizedPhone));
  }, [user.phone]);

  const userAddress = isLoadingAddresses
    ? null
    : addresses.find(
        (address) => String(address._id) === String(savedAddresses),
      );

  const calculation = useOrderCalculation({
    availableBasketItems,
    userAddress,
    deliveryPrice: deliveryDetail.deliveryPrice || 0,
    branchId,
    isPickupMode: isPickup,
  });

  // Списание баллов, промокод и строки итога — только на чекауте, над
  // способом оплаты. Один элемент на обе ветки (доставка и самовывоз).
  const orderCalculationCard = (
    <OrderCalculationCard calculation={calculation} isPickupMode={isPickup} />
  );

  // Pickup checkout is reached from the basket just like delivery is
  // (`proceedToCheckout` in Basket), so it needs the same way back.
  useTelegramBackButton("/cart");

  // Самовывоз оплачивается только наличными, выбора там нет — эту ветку
  // правка EVSTG-54 не трогает, всё ниже про «Оплату» относится к доставке.
  const cashPayment = useMemo(
    () => resolveCashPayment(paymentList),
    [paymentList],
  );

  // Что показываем в «Оплате» доставки: ровно список бэкенда. Единственное, что
  // убираем сверх него, — наличные, если сумма заказа выше лимита наличных
  // (его тоже отдаёт бэкенд, branch/max-cash-amount).
  const paymentMethods = useMemo(
    () =>
      paymentList.filter(
        (method) => getPaymentKind(method) !== "cash" || amount >= totalPrice,
      ),
    [paymentList, amount, totalPrice],
  );
  const selectedPaymentMethod = paymentSelection
    ? paymentMethods.find((method) => method.id === paymentSelection.paymentId)
    : undefined;

  // Сменился филиал или тип заказа — пришёл новый список. Выбор, которого в
  // нём нет (или карты, которую удалили), сбрасываем: пользователь выбирает
  // заново из актуального. Пока новый список грузится, оформление и так
  // закрыто (см. handlePayment), поэтому ждём его, а не сбрасываем вслепую.
  useEffect(() => {
    if (!paymentSelection || !isPaymentListReady) return;

    const cardGone =
      paymentSelection.cardId !== null &&
      !cardList?.some((card) => card.id === paymentSelection.cardId);

    if (!selectedPaymentMethod || cardGone) setPaymentSelection(null);
  }, [paymentSelection, selectedPaymentMethod, isPaymentListReady, cardList]);

  // Единственный способ (не карта — там ещё выбирать саму карту) выбираем
  // сразу: спрашивать «выберите способ оплаты» из одного варианта незачем.
  useEffect(() => {
    if (paymentSelection || !isPaymentListReady) return;
    if (paymentMethods.length !== 1) return;

    const [only] = paymentMethods;
    if (getPaymentKind(only) !== "card") {
      setPaymentSelection({ paymentId: only.id, cardId: null });
    }
  }, [paymentSelection, isPaymentListReady, paymentMethods]);

  // Блок «Оплата» доставки: состав решает бэкенд.
  const paymentSection = (
    <PaymentMethods
      methods={paymentMethods}
      isLoading={isLoadingPayments}
      isError={isPaymentListError}
      onRetry={() => refetchPaymentList()}
      cardList={cardList ?? []}
      selection={paymentSelection}
      onSelect={setPaymentSelection}
      onAddCard={() => setOpen(true)}
    />
  );
  const canPlaceOrder =
    isPickup || (isPaymentListReady && paymentMethods.length > 0);

  const pickupReadyAt = useMemo(() => {
    const minutes = deliveryDetail?.deliveryTime || 15;
    const readyAt = new Date(Date.now() + minutes * 60000);

    return `${String(readyAt.getHours()).padStart(2, "0")}:${String(
      readyAt.getMinutes(),
    ).padStart(2, "0")}`;
  }, [deliveryDetail?.deliveryTime]);

  // The branch's working hours bound the schedulable window; if it's
  // currently closed, the window starts at the next opening instead of now.
  const deliveryWorkWindow = useMemo(
    () =>
      getActiveWorkWindow(
        new Date(),
        deliveryDetail?.workTimeFrom,
        deliveryDetail?.workTimeTo,
      ),
    [deliveryDetail?.workTimeFrom, deliveryDetail?.workTimeTo],
  );
  // Earliest schedulable moment: prep/delivery estimate plus a fixed buffer,
  // never earlier than the branch actually being open. Считается от «сейчас»
  // на каждом рендере, а не мемоизируется: страница может быть открыта долго,
  // и запомненный минимум уехал бы в прошлое — пикер дал бы выбрать время
  // раньше допустимого.
  const deliveryMinTime = (() => {
    const earliest = new Date(
      Date.now() + ((deliveryDetail?.deliveryTime || 15) + 30) * 60000,
    );
    if (deliveryWorkWindow && earliest < deliveryWorkWindow.start) {
      return deliveryWorkWindow.start;
    }
    return earliest;
  })();
  function formatDeliveryWindow(deliveryTime: number): string {
    const now = new Date();

    const end = new Date(now.getTime() + deliveryTime * 60000);
    const endHour = end.getHours().toString().padStart(2, "0");
    const endMin = end.getMinutes().toString().padStart(2, "0");

    const start = new Date(now.getTime() + deliveryTime * 80000);
    const startHour = start.getHours().toString().padStart(2, "0");
    const startMin = start.getMinutes().toString().padStart(2, "0");

    return `${t("delivery_window")} ${endHour}:${endMin}-${startHour}:${startMin}`;
  }

  const handlePayment = async () => {
    const timeIssueNow = validateScheduledTime({
      isPickup,
      pickupOn: pickup.pickupOn ?? null,
      deliveryOn,
      deliveryTimeMinutes: deliveryDetail?.deliveryTime,
      workTimeFrom: deliveryDetail?.workTimeFrom,
      workTimeTo: deliveryDetail?.workTimeTo,
    });
    if (timeIssueNow) {
      setTimeIssue(timeIssueNow);
      if (isPickup) {
        setShowPickupTimePicker(true);
      } else {
        setShowTimePicker(true);
      }
      return;
    }

    // Доставка: оформлять можно только способом из актуального
    // branch/payment-list — пока список не получен (грузится, ошибка) или
    // пуст, нельзя совсем.
    if (!isPickup && (!isPaymentListReady || !paymentMethods.length)) {
      window.Telegram.WebApp.showAlert(t("payment-list-unavailable"));
      return;
    }

    // Pickup is cash-only, so there's nothing for the user to choose and
    // nothing to validate — settle it here and skip the card path entirely.
    const paymentMethod = isPickup ? cashPayment : selectedPaymentMethod;
    const isCardPayment =
      !isPickup && !!paymentMethod && getPaymentKind(paymentMethod) === "card";

    if (
      !isPickup &&
      (!paymentMethod || (isCardPayment && !paymentSelection?.cardId))
    ) {
      window.Telegram.WebApp.showAlert(t("checkouts.select_payment"));
      return;
    }

    // У заказа нет отдельного поля под «на вынос / в ресторане», поэтому способ
    // получения уезжает первым в комментарии — так его видит оператор. Подписи
    // намеренно русские: комментарий читают на кухне, а не в интерфейсе
    // пользователя, и он может быть на любом языке.
    const trimmedComment = comment.trim();
    const receivingLabel = isPickup
      ? receivingMode === "takeaway"
        ? "На вынос"
        : "В ресторане"
      : "";
    const orderComment = [receivingLabel, trimmedComment]
      .filter(Boolean)
      .join(", ");
    // Приборы/салфетки/сахар едут отдельным полем, а не в комментарии: у
    // заказа есть под них consumables.
    const consumables = getStoredOrderExtras();

    // Nothing is delivered for a pickup order, so it carries no delivery fee.
    const deliveryCost = isPickup
      ? 0
      : (calculatedDeliveryPrice ?? deliveryDetail.deliveryPrice ?? 0);
    const checkoutTotalPrice = isPickup
      ? totalPrice
      : (calculatedTotalPrice ?? totalPrice + deliveryCost);

    await createOrder({
      type: orderType,
      address: isPickup
        ? {
            title: pickup.branch.address || pickup.branch.title,
            // "lat,lon" — the order the backend reads geo in, same as how
            // addresses are saved. Sending it lon-first put the branch in the
            // Barents Sea. Delivery below is deliberately left as-is.
            geo: `${pickup.branch.location.lat},${pickup.branch.location.lon}`,
          }
        : {
            title: userAddress?.title || "",
            geo: `${userAddress?.location.lon},${userAddress?.location.lat}`,
          },
      positions: basketItems
        .filter((item) => item.isAvailable !== false)
        .map((item) => ({
          id: item.id,
          price: String(getBasketItemUnitPrice(item)),
          title: item.title,
          type: "Org",
          amount: item.quantity,
          modifiers: item.selectedModifiers?.map((modifier) => ({
            modifier_group_id: modifier.groupId,
            modifier_item_id: modifier.itemSku || modifier.item?.sku || modifier.itemId,
            quantity: modifier.quantity,
            price: modifier.price,
          })),
        })),
      branch_id: branchId,
      payment_id: String(paymentMethod?.id || 58),
      dop_nomer: "",
      card_id: isCardPayment ? (paymentSelection?.cardId ?? undefined) : undefined,
      deliveryOn: (isPickup ? undefined : deliveryOn) || undefined,
      pickupTime:
        isPickup && pickup.pickupOn
          ? format(new Date(pickup.pickupOn), "yyyy-MM-dd HH:mm")
          : undefined,
      deliveryCost,
      totalPrice: checkoutTotalPrice,
      bonusPay: activePromoCode ? 0 : selectedBonusPay,
      promoCode: activePromoCode,
      phone_number_alt: recipientPhone,
      comment: orderComment,
      consumables,
      client_id: user._id!,
      analytics: {
        payment_type: paymentMethod && getPaymentAnalyticsType(paymentMethod),
      },
    });
  };

  const allPrice = calculation.summary.lastPrice;

  if (!userAddress && !isPickup) {
    navigate("/profile/addresses");
  }
  if (!basketItems) {
    navigate("/");
  }
  if (
    // Самовывозу список нужен до отрисовки, как и раньше: из него берутся
    // наличные. Доставка показывает загрузку прямо в блоке «Оплата».
    (isPickup && isLoadingPayments) ||
    isLoadingAddresses ||
    isLoadingCardList ||
    isLoadingMaxAmount
  ) {
    return (
      <div className="text-center h-full flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin" />
      </div>
    );
  }

  return (
    <>
      {/* Column layout, not a plain scroll box: `sticky bottom-0` only pins the
          pay button when the content overflows, so on short pages (pickup) it
          drifted up under the last card. */}
      <div className="h-screen flex flex-col bg-fill-background">
        <div className="flex-1 overflow-y-auto">
        <div className="pt-safe-top bg-brand-green ">
          <div className="flex flex-col space-y-5 p-4 bg-fill-background ">
            <p className="text-center text-display-sm pt-2">{t("checkout")}</p>
            {isPickup ? (
              <div className="flex flex-col space-y-2">
                <Card className="rounded-2xl flex flex-col pt-1 pb-2 space-y-2">
                  <p className="px-4 text-display-sm pt-2">
                    {t("pickup-address")}
                  </p>
                  <div className="py-2 px-4 flex flex-row space-x-2">
                    <div className="w-9 h-9 shrink-0 bg-fill-background rounded-[10px] flex items-center justify-center">
                      <LocationFooterIcon className="text-text-icon-secondary h-5 w-5" />
                    </div>
                    <div className="flex flex-col gap-y-[2px]">
                      <p className="text-body-sm-medium">
                        {pickup.branch.address || pickup.branch.title}
                      </p>
                      {/* Название дублирует адрес, когда адреса у филиала нет —
                          показываем вторую строку только если она что-то добавляет. */}
                      {!!pickup.branch.address && !!pickup.branch.title && (
                        <p className="text-caption-sm-medium text-text-tertiary">
                          {pickup.branch.title}
                        </p>
                      )}
                    </div>
                  </div>
                </Card>

                <Card className="rounded-2xl flex flex-col pt-1 pb-2 space-y-2">
                  <div className="flex flex-row items-center justify-between px-4 pt-2">
                    <p className="text-display-sm">{t("pickup-time-title")}</p>
                    <Button
                      onClick={() => setShowPickupTimePicker(true)}
                      className="bg-fill-background text-text-primary rounded-full shadow-none"
                    >
                      {t("choose_time")}
                    </Button>
                  </div>
                  <div className="py-2 px-4 flex flex-row space-x-2">
                    <div className="w-9 h-9 shrink-0 bg-fill-background rounded-[10px] flex items-center justify-center">
                      <ClockIcon className="text-text-icon-secondary" />
                    </div>
                    <div className="flex flex-col gap-y-[2px]">
                      <p className="text-body-sm-medium">
                        {pickup.pickupOn
                          ? `${t("pickup_window")} ${format(new Date(pickup.pickupOn), "HH:mm")}`
                          : `Приготовим к ~${pickupReadyAt}`}
                      </p>
                      <p className="text-caption-sm-medium text-text-tertiary">
                        {pickup.pickupOn
                          ? t("custom_delivery_time")
                          : t("fast-delivery")}
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="rounded-2xl flex flex-col pt-1 pb-4">
                  <p className="px-4 text-display-sm pt-2">
                    {t("order-receiving")}
                  </p>
                  <div className="mt-2 flex flex-row items-center gap-x-2 px-4">
                    {(
                      [
                        ["takeaway", "receiving-takeaway"],
                        ["in-place", "receiving-in-place"],
                      ] as const
                    ).map(([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => {
                          impactOccuer("soft");
                          setReceivingMode(mode);
                        }}
                        className={cn(
                          // Ширина по содержимому: подписи разной длины и
                          // меняются с языком, фиксировать её нельзя.
                          "h-8 shrink-0 rounded-full px-[10px] text-body-sm-medium transition-colors",
                          receivingMode === mode
                            ? "bg-brand-green text-text-white"
                            : "bg-fill-background text-text-primary",
                        )}
                      >
                        {t(label)}
                      </button>
                    ))}
                  </div>
                </Card>

                <Card className="rounded-2xl p-4">
                  <label className="flex flex-row gap-x-3">
                    <CommentIcon className="mt-[2px] shrink-0 text-text-icon-secondary" />
                    <textarea
                      value={comment}
                      onChange={(event) => setComment(event.target.value)}
                      placeholder={t("order-comment-placeholder")}
                      rows={3}
                      className="min-h-[75px] w-full resize-none bg-transparent text-body-sm-medium text-text-primary outline-none placeholder:text-text-tertiary"
                    />
                  </label>
                </Card>

                {orderCalculationCard}

                <Card className="rounded-2xl p-4 flex flex-col space-y-3">
                  <p className="text-display-sm">{t("payment-method")}</p>
                  <div className="flex flex-row items-center space-x-3">
                    <div className="w-9 h-9 shrink-0 bg-fill-background rounded-[10px] flex items-center justify-center">
                      <WalletIcon className="text-text-icon-secondary" />
                    </div>
                    <p className="text-body-sm-medium">{t("pay-on-pickup")}</p>
                  </div>
                </Card>
              </div>
            ) : (
            <div className="flex flex-col space-y-2">
              <Card className="rounded-2xl flex flex-col pt-1 pb-2 space-y-2">
                <p className="px-4 text-display-sm pt-2">
                  {t("delivery-address")}
                </p>
                <div className="py-2 px-4 flex flex-row space-x-2">
                  <div className="w-9 h-9 bg-fill-background rounded-[10px] flex items-center justify-center">
                    <LocationFooterIcon className="text-text-icon-secondary h-5 w-5" />
                  </div>
                  <div className="flex flex-col gap-y-[2px]">
                    <p className="text-body-sm-medium">{userAddress?.title}</p>
                    <p className="text-caption-sm-medium text-text-tertiary">
                      {userAddress?.title}
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="rounded-2xl flex flex-col pt-1 pb-2 space-y-2">
                <div className="flex flex-row items-center justify-between px-4 pt-2">
                  <p className="text-display-sm">{t("delivery-time")}</p>
                  <Button
                    onClick={() => setShowTimePicker(true)}
                    className="bg-fill-background text-text-primary rounded-full shadow-none"
                  >
                    {t("choose_time")}
                  </Button>
                </div>
                <div className="py-2 px-4 flex flex-row space-x-2">
                  <div className="w-9 h-9 bg-fill-background rounded-[10px] flex items-center justify-center">
                    <ClockIcon className="text-text-icon-secondary " />
                  </div>
                  <div className="flex flex-col gap-y-[2px]">
                    <p className="text-body-sm-medium">
                      {deliveryOn
                        ? format(new Date(deliveryOn), "yyyy-MM-dd HH:mm")
                        : formatDeliveryWindow(
                            deliveryDetail?.deliveryTime || 0,
                          )}
                    </p>
                    <p className="text-caption-sm-medium text-text-tertiary">
                      {deliveryOn
                        ? t("custom_delivery_time")
                        : t("fast-delivery")}
                    </p>
                  </div>
                </div>
              </Card>
              <Card
                onClick={() => {
                  setPhoneDraft(getPhoneDigits(recipientPhone));
                  setPhoneDrawerOpen(true);
                }}
                className="rounded-2xl flex items-center justify-between px-4 py-4 cursor-pointer"
              >
                <div className="flex min-w-0 flex-row items-center space-x-4">
                  <div className="w-9 h-9 flex shrink-0 items-center justify-center">
                    <PhoneIcon className="text-text-primary h-5 w-5" />
                  </div>
                  <div className="flex min-w-0 flex-col gap-y-[2px]">
                    <p className="text-body-sm-medium text-text-secondary">
                      {t("recipient-phone")}
                    </p>
                    <p className="truncate text-[24px] font-semibold leading-7 text-text-primary">
                      {formatRecipientPhone(recipientPhone)}
                    </p>
                  </div>
                </div>
                <ChevronRight className="h-6 w-6 shrink-0 text-text-primary" />
              </Card>
              
              <Card className="rounded-2xl p-4">
                <label className="flex flex-col gap-2">
                  <span className="text-display-sm">{t("comment")}</span>
                  <textarea
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder={t("order-comment-placeholder")}
                    rows={3}
                    className="min-h-[75px] w-full resize-none rounded-xl bg-fill-background p-3 text-body-sm-medium text-text-primary outline-none placeholder:text-text-tertiary"
                  />
                </label>
              </Card>
              {orderCalculationCard}
              {paymentSection}
            </div>
            )}
          </div>
        </div>
        <SavaeCardDrawer drawerOpen={open} setDrawerOpen={setOpen} />
        <Drawer open={phoneDrawerOpen} onOpenChange={setPhoneDrawerOpen}>
          <DrawerContent className="border-none px-6 pb-safe-bottom">
            <div className="flex flex-col gap-4 pb-8 pt-4">
              <DrawerTitle className="sr-only">
                {t("recipient-phone")}
              </DrawerTitle>
              <div className="flex flex-row items-start gap-4">
                <div className="flex h-[78px] w-[31%] min-w-[108px] max-w-[145px] shrink-0 items-center rounded-[20px] bg-fill-base px-5">
                  <span className="text-[30px] font-semibold leading-none text-text-primary">
                    +998
                  </span>
                </div>
                <label className="relative flex h-[78px] min-w-0 flex-1 items-end rounded-[20px] bg-fill-base px-5 pb-5">
                  
                  <input
                    ref={phoneMask.inputRef}
                    value={phoneMask.value}
                    onChange={phoneMask.onChange}
                    onKeyDown={phoneMask.onKeyDown}
                    inputMode="numeric"
                    autoFocus
                    className="w-full bg-transparent pr-7 text-[26px] font-semibold leading-none text-text-primary outline-none min-[370px]:text-[30px]"
                  />
                  {phoneDraft && (
                    <button
                      type="button"
                      onClick={() => setPhoneDraft("")}
                      className="absolute right-4 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-text-tertiary text-fill-base"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  )}
                </label>
              </div>
              <div className="h-px w-full bg-border" />
              <Button
                disabled={phoneDraft.length !== 9}
                onClick={() => {
                  setRecipientPhone(`+998${phoneDraft}`);
                  setPhoneDrawerOpen(false);
                }}
                className="mx-auto h-16 min-w-[164px] rounded-[16px] bg-button-primary px-7 text-[24px] font-semibold text-text-white disabled:opacity-50"
              >
                {t("change")}
              </Button>
            </div>
          </DrawerContent>
        </Drawer>
        <Drawer
          open={showTimePicker}
          onOpenChange={(open) => {
            setShowTimePicker(open);
            if (!open) setTimeIssue(null);
          }}
        >
          <DrawerContent className="border-none px-6 pb-safe-bottom max-h-[70vh]">
            <div className="flex flex-col gap-3 pb-4 pt-2">
              <DrawerTitle className="sr-only">{t("delivery-time")}</DrawerTitle>
              {timeIssue && (
                <p className="text-caption-sm-medium text-red-500 text-center">
                  {t(timeIssue === "too-soon" ? "time-too-soon" : "time-too-late")}
                </p>
              )}
              <DeliveryTimePicker
                value={deliveryOn || undefined}
                minDateTime={deliveryMinTime}
                maxDateTime={deliveryWorkWindow?.end ?? null}
                onSelect={(dateTime) => {
                  setDeliveryOn(dateTime);
                  setShowTimePicker(false);
                  setTimeIssue(null);
                }}
                onCancel={() => {
                  setDeliveryOn(null);
                  setShowTimePicker(false);
                  setTimeIssue(null);
                }}
              />
            </div>
          </DrawerContent>
        </Drawer>
        <Drawer
          open={showPickupTimePicker}
          onOpenChange={(open) => {
            setShowPickupTimePicker(open);
            if (!open) setTimeIssue(null);
          }}
        >
          <DrawerContent className="border-none px-6 pb-safe-bottom max-h-[70vh]">
            <div className="flex flex-col gap-3 pb-4 pt-2">
              <DrawerTitle className="sr-only">{t("pickup-time")}</DrawerTitle>
              {timeIssue && (
                <p className="text-caption-sm-medium text-red-500 text-center">
                  {t(timeIssue === "too-soon" ? "time-too-soon" : "time-too-late")}
                </p>
              )}
              <DeliveryTimePicker
                value={pickup.pickupOn || undefined}
                minDateTime={getPickupMinTime(new Date())}
                maxDateTime={getPickupMaxTime(new Date())}
                mode="pickup"
                onSelect={(dateTime) => {
                  dispatch(setPickupOn(dateTime));
                  setShowPickupTimePicker(false);
                  setTimeIssue(null);
                }}
                onCancel={() => {
                  setShowPickupTimePicker(false);
                  setTimeIssue(null);
                }}
              />
            </div>
          </DrawerContent>
        </Drawer>
        </div>
      <div className="shrink-0 bg-fill-base pt-3 pb-[calc(var(--tg-content-safe-area-inset-bottom)+var(--tg-safe-area-inset-bottom)+16px)] px-4">
        <div className="flex h-12 flex-row items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col">
            {user.isAuth && calculation.isCalculatingCart ? (
              <Skeleton className="h-5 w-24 rounded-md" />
            ) : (
              <p className="text-display-sm text-text-primary">
                {formatPrice(allPrice)} {t("sum")}
              </p>
            )}
            {calculation.summary.earnedAmount > 0 &&
            !calculation.isCalculatingCart ? (
              <p className="text-caption-sm-medium text-brand-green">
                +{formatPrice(calculation.summary.earnedAmount)} {t("point")}
              </p>
            ) : null}
          </div>
          <LoadingButton loading={isPending} type="submit" asChild>
            <Button
              onClick={handlePayment}
              // Свой disabled у Button перекрывает тот, что ставит
              // LoadingButton (Slot отдаёт приоритет пропсам ребёнка), поэтому
              // isPending учитываем здесь.
              disabled={!canPlaceOrder || isPending}
              type="submit"
              className="h-12 shrink-0 rounded-[32px] bg-button-primary px-6 text-white text-base font-semibold"
            >
              {t("checkout")}
            </Button>
          </LoadingButton>
        </div>
      </div>
      </div>
    </>
  );
};
export default Checkout;
